import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from 'material-ui/styles';
import classNames from 'classnames';
import Button from 'material-ui/Button';
import Typography from "material-ui/Typography";
import { isFreePlan } from '../../../utils/planUtils';
import { isEnableLetterAI } from '../../../utils/userOptionUtils';
import { CircularProgress } from "material-ui/Progress";

const styles = () => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
    background: '#FFFFFF 0% 0% no-repeat padding-box',
    backgroundColor: '#FFFFFF',
    '-webkit-box-shadow': '0px 0px 3px 0px rgba(0,0,0,0.15)',
    '-moz-box-shadow': '0px 0px 3px 0px rgba(0,0,0,0.15)',
    boxShadow: '0px 0px 3px 0px rgba(0,0,0,0.15)',
    padding: "60px 40px"
  },

  img: {
    width: '108px',
    height: '108px',
  },

  title: {
    width: '100%',
    textAlign: 'center',
    color: '#5090FF',
    fontSize: '16px',
    fontWeight: 'bold',
  },

  inputLetterContents: {
    fontSize: "11px",
    lineHeight: "1.5rem",
    border: "1px solid #D9DBDF",
    borderRadius: "2px",
    marginTop: '60px',
    width: 'calc(100% - 44px)',
    padding: '20px',
    "&::placeholder": {
      color: '#A2A2A2',
    },
    height: '315px',
    resize: 'none',
  },

  description: {
    fontSize: "11px",
    width: "100%",
    marginTop: "20px",
  },

  btn: {
    marginTop: '40px',
    padding: '15px 0',
    width: "100%",
    borderRadius: "2px",
    fontSize: "16px",
  },

  btnDisable: {
    border: "1px solid #ECEDEF",
    background: "#ECEDEF",
    color: "#000000",
  },

  btnEnable: {
    border: "1px solid #1A65FF",
    background: "#FFFFFF",
    color: "#1A65FF",
    "&:hover": {
      background: "#1A65FF",
      color: '#FFFFFF',
    },
  },

  corrected: {
    width: "100%",
  },

  result: {
    marginTop: '40px',
    background: "#ECEDEF",
    width: "100%",
  },

  resultContent: {
    fontSize: "11px",
    lineHeight: "1.5rem",
    padding: "20px",
    whiteSpace: "pre-wrap",
  },

  point: {
    fontSize: "11px",
    lineHeight: "1.5rem",
    whiteSpace: "pre-wrap",
    marginTop: '30px',
  },
});

class LetterCorrectionPanel extends React.Component {
  initialState = {
    contents: "",
    progress: 0,
    progressIntervalId: null,
  }

  constructor(props) {
    super(props);
    this.state = { ...this.initialState };
  }

  componentDidUpdate(prevProps) {
    const aboutMaxPercent = 80;
    const maxPercent = 100;
    const randomInt = 3;
    const interval = 1000;
    const addRandomInt = 8;
    const baseInt = 4;
    const finInterval = 800;
    const successInterval = 100;

    // 添削前 -> 添削中になった場合
    if ((prevProps.isCorrecting === false && this.props.isCorrecting === true)) {
      // プログレスの値を0にする
      this.setState({
        progress: 0,
      }, () => {
        // 1秒ごとにランダムで0~3秒プログレスの値を加算するタイマー 最大 79 + (0 or 1 or 2 or 3)
        const progressIntervalId = setInterval(() => {
          this.setState({
            progress: this.state.progress + ((this.state.progress < aboutMaxPercent) ? (Math.floor(Math.random() * randomInt)) : 0),
          });
        }, interval);
        // 処理終了後にステートに消せるようにstate入れておく
        this.setState({
          progressIntervalId: progressIntervalId,
        });
      });
    }

    // 添削中 -> 添削終了になった場合
    if ((prevProps.isCorrecting === true && this.props.isCorrecting === false)) {
      // タイマーをクリアする
      clearInterval(this.state.progressIntervalId);

      // 添削成功した場合
      if (this.hasCorrectedContents()) {
        // 100%までの残りの%を緩やかに100%にさせる
        const progressRemainingInterval = setInterval(() => {
          let remainingPercent = maxPercent - this.state.progress;

          // 0.1秒ごとにランダムで4~12秒プログレスの値を加算する
          let rand = Math.floor(Math.random() * addRandomInt) + baseInt;
          // 乱数が残りの時間が超える場合はそのまま残りの時間を乱数とする 例: 残り3% 乱数5 => 3
          if (rand > remainingPercent) {
            rand = remainingPercent;
          }

          this.setState({
            progress: this.state.progress + rand,
          }, () => {
            // 100%であれば終了処理のタイマーを0.8秒余韻を残してクリア
            if (this.state.progress === maxPercent) {
              clearInterval(progressRemainingInterval);
              setTimeout(() => {
                this.setState({
                  progressIntervalId: null,
                });
              }, finInterval);
            }
          });
        }, successInterval);
      } else {
        // 失敗した場合は即時にタイマーをstateから外す
        this.setState({
          progressIntervalId: null,
        });
      }
    }
  }

  handleChange = e => {
    this.setState({
      contents: e.target.value,
    });
  }

  isEditDisable = () => {
    return this.props.isCorrecting || this.state.progressIntervalId;
  }

  hasCorrectedContents = () => {
    return this.props.correctedContents["result"] &&
      this.props.correctedContents["result"] !== "" &&
      this.props.correctedContents["point"] &&
      this.props.correctedContents["point"] !== "";
  }

  isCorrectRequestDisable = () => {
    return this.state.contents === "" || this.isEditDisable();
  }

  isDisplayResult = () => {
    return this.hasCorrectedContents() && !this.isEditDisable();
  }

  handleClick = () => {
    if (isFreePlan(this.props.user)) {
      this.props.togglePlanDialog();
    } else if (!isEnableLetterAI(this.props.user)) {
      this.props.togglePromotePlanChangeDialog();
    } else {
      this.props.postCorrect({ "contents": this.state.contents });
    }
  }

  render() {
    const { classes } = this.props;

    return (
      <div className={classes.root}>
        <img
          src={require(`../../../assets/images/letter-correction.png`)}
          alt="文章添削"
          className={classes.img}
        />
        <div className={classes.title} style={{ marginTop: '30px' }}>
          レターの文面を添削する
        </div>
        <textarea value={this.state.contents} placeholder="レターの文面を入力してください" disabled={this.isEditDisable()} className={classes.inputLetterContents} onChange={(e) => this.handleChange(e)}>
          {this.state.contents}
        </textarea>
        <Button
          className={classNames(classes.btn, this.isCorrectRequestDisable() ? classes.btnDisable : classes.btnEnable)}
          onClick={this.handleClick}
          disabled={Boolean(this.isCorrectRequestDisable())}
        >
          {(this.isEditDisable()) ?
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <span>
                {`添削中です (${this.state.progress}%)`}
              </span>
              <div style={{ position: "relative", display: "flex", alignItems: "center", marginLeft: "10px" }}>
                <CircularProgress
                  style={{ color: "#6F9EFF", height: "18px", width: "18px", position: "relative" }}
                />
              </div>
            </div>
            :
            <span>{"添削"}</span>
          }
        </Button>
        <Typography
          variant="body2"
          className={classes.description}
        >
          <div>
            ※添削結果は、商談率の高い文面を約束するものではありません。
          </div>
        </Typography>
        {
          this.isDisplayResult() &&
          (
            <div className={classes.corrected}>
              <div className={classes.result}>
                <div className={classes.resultContent}>{this.props.correctedContents["result"]}</div>
              </div>
              {/* <Button className={classNames(classes.btn, classes.btnEnable)}>Wordファイル(.docx)をダウンロード</Button> */}
              <div className={classes.title} style={{ marginTop: '60px' }}>
                詳しい添削内容
              </div>
              <div className={classes.point}>{this.props.correctedContents["point"]}</div>
            </div>
          )
        }
      </div>
    );
  }
}

LetterCorrectionPanel.propTypes = {
  classes: PropTypes.object.isRequired,
  user: PropTypes.object,
  isCorrecting: PropTypes.bool,
  postCorrect: PropTypes.func.isRequired,
  togglePlanDialog: PropTypes.func.isRequired,
  togglePromotePlanChangeDialog: PropTypes.func.isRequired,
  correctedContents: PropTypes.object,
};

export default withStyles(styles)(LetterCorrectionPanel);



// WEBPACK FOOTER //
// ./src/components/LetterCorrection/panel/LetterCorrectionPanel.jsx